import { Link } from "react-router-dom";
import { FileText, Download, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { FREE_RESOURCES as RESOURCES } from "@/lib/site";

function ResourceCard({ resource, index }) {
  return (
    <Reveal delay={0.08 * index} className="h-full">
      <div
        className="flex flex-col h-full bg-white border border-line rounded-md overflow-hidden hover:shadow-[0_24px_48px_-24px_rgba(11,21,34,0.25)] transition-shadow duration-300"
        data-testid={`free-resources-card-${resource.id}`}
      >
        <div className="p-10 bg-navy-deep flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-12 h-12 text-gold mx-auto" />
            <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-gold">
              Free PDF Guide
            </p>
          </div>
        </div>
        <div className="p-8 flex-1 flex flex-col">
          <h2 className="font-serif text-xl sm:text-2xl text-navy leading-snug">
            {resource.title}
          </h2>
          {resource.subtitle && (
            <p className="mt-2 font-serif italic text-gold-hover">
              {resource.subtitle}
            </p>
          )}
          <div className="mt-5 space-y-4 text-sm leading-relaxed text-stone2 flex-1">
            {resource.description.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {resource.file ? (
              <>
                <Link
                  to={`/free-resources/preview/${resource.id}`}
                  data-testid={`free-resources-preview-${resource.id}`}
                  className="inline-flex items-center justify-center gap-2 bg-navy text-ivory px-5 py-3 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
                >
                  Preview PDF <FileText className="w-4 h-4" />
                </Link>
                <a
                  href={resource.file}
                  download
                  data-testid={`free-resources-download-${resource.id}`}
                  className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-3 rounded-full text-sm font-semibold hover:bg-gold-hover hover:text-ivory transition-colors duration-200"
                >
                  Download PDF <Download className="w-4 h-4" />
                </a>
              </>
            ) : (
              <>
                <span
                  aria-disabled="true"
                  data-testid={`free-resources-preview-${resource.id}-disabled`}
                  className="inline-flex items-center justify-center gap-2 bg-navy text-ivory px-5 py-3 rounded-full text-sm font-medium opacity-40 cursor-not-allowed select-none"
                >
                  Preview PDF <FileText className="w-4 h-4" />
                </span>
                <span
                  aria-disabled="true"
                  data-testid={`free-resources-download-${resource.id}-disabled`}
                  className="inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-3 rounded-full text-sm font-semibold opacity-40 cursor-not-allowed select-none"
                >
                  Download PDF <Download className="w-4 h-4" />
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function FreeResources() {
  return (
    <div className="bg-ivory" data-testid="free-resources-page">
      <section className="mx-auto max-w-6xl px-5 sm:px-8 pt-36 pb-16 sm:pt-44">
        <Reveal>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
            Free Resources
          </h1>
          <p className="mt-6 font-serif italic text-lg sm:text-xl text-gold max-w-3xl">
            Compassionate Guidance for Patients, Families, Caregivers, and
            Healthcare Professionals
          </p>
          <div className="mt-8 max-w-3xl space-y-5 text-lg leading-relaxed text-stone2">
            <p>
              The journey through serious illness, hospice care, caregiving,
              and grief often brings questions we never expected to ask and
              emotions we may struggle to understand.
            </p>
            <p>
              These free educational resources offer practical guidance,
              thoughtful reflection, and compassionate encouragement for
              patients, families, caregivers, and the healthcare professionals
              who walk alongside them.
            </p>
            <p>
              Created by Chaplain Derrick Hall, author of From Diagnosis to
              Goodbye, each guide is designed to encourage meaningful
              conversations, support emotional well-being, strengthen
              understanding, and help individuals navigate difficult moments
              with dignity and compassion.
            </p>
          </div>
        </Reveal>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="free-resources-section-serious-illness"
      >
        <Reveal>
          <div className="mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-navy tracking-tight leading-snug">
              Serious Illness & Family Conversations
            </h2>
            <div className="mt-5 h-px w-16 bg-gold" />
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {RESOURCES.filter((r) => r.section === "serious-illness").map(
            (r, i) => (
              <ResourceCard key={r.id} resource={r} index={i} />
            )
          )}
        </div>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="free-resources-section-grief"
      >
        <Reveal>
          <div className="mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-navy tracking-tight leading-snug">
              Grief, Bereavement & Emotional Support
            </h2>
            <div className="mt-5 h-px w-16 bg-gold" />
          </div>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {RESOURCES.filter((r) => r.section === "grief-bereavement").map(
            (r, i) => (
              <ResourceCard key={r.id} resource={r} index={i} />
            )
          )}
        </div>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="free-resources-continue"
      >
        <Reveal>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-serif text-2xl sm:text-3xl text-navy tracking-tight">
              Continue the Journey
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-stone2">
              These free guides are companions to{" "}
              <em>From Diagnosis to Goodbye</em>: Restoring the
              Patient&rsquo;s Voice Through Every Stage of the Journey.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-stone2">
              For deeper guidance on serious illness, hospice care, family
              communication, caregiving, and grief, explore the complete book
              and companion workbooks.
            </p>
            <Link
              to="/books"
              data-testid="free-resources-explore-books"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-navy text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-navy-light transition-colors duration-200"
            >
              Explore the Books & Workbooks <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-6xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="bg-navy-deep rounded-md p-10 sm:p-16 text-center">
            <div className="max-w-2xl mx-auto">
              <p className="text-xs uppercase tracking-[0.24em] text-gold font-medium">
                From Diagnosis to Goodbye &mdash; Educational Resources
              </p>
              <p className="mt-6 font-serif text-2xl sm:text-3xl text-ivory leading-snug">
                Created by Chaplain Derrick Hall
              </p>
              <p className="mt-5 font-serif italic text-lg sm:text-xl text-gold">
                Never allow the diagnosis to become louder than the
                patient&rsquo;s voice.
              </p>
              <p className="mt-8 text-xs leading-relaxed text-ivory/50">
                These guides are offered for educational and supportive
                purposes only. They do not replace medical, legal, or
                professional advice. For decisions about care, please speak
                with the patient&rsquo;s healthcare team or a qualified
                professional.
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section
        className="mx-auto max-w-6xl px-5 sm:px-8 pb-24"
        data-testid="free-resources-copyright"
      >
        <Reveal>
          <div className="border-t border-line pt-10 text-center max-w-3xl mx-auto">
            <p className="text-sm font-medium text-navy">
              Copyright &copy; 2026 Derrick Hall. All rights reserved.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-stone2">
              These resources are provided free of charge for personal and
              educational use. They may be downloaded and shared in their
              original, unaltered form with proper attribution to Derrick
              Hall. Modification, commercial reproduction, or resale requires
              prior written permission.
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
